import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Factory, Pencil, Plus } from 'lucide-react';
import { api, type Pagina } from '@/shared/api/cliente';
import { useSesion } from '@/shared/sesion/SesionProvider';
import { BotonDeFila } from '@/shared/ui/BotonDeFila';
import { CampoDeBusqueda } from '@/shared/ui/CampoDeBusqueda';
import { EncabezadoDePagina } from '@/shared/ui/EncabezadoDePagina';
import { Tabla, type Columna } from '@/shared/ui/Tabla';
import { useValorDiferido } from '@/shared/ui/useValorDiferido';
import { ModalDeGenerador } from './ModalDeGenerador';
import { enUnidad, type CategoriaDeGenerador, type Generador, type Tarifario } from './contrato';

export function PaginaGeneradores() {
  const [texto, setTexto] = useState('');
  const [categoria, setCategoria] = useState('');
  const [estado, setEstado] = useState('true');
  const [pagina, setPagina] = useState(0);
  const [tamanio, setTamanio] = useState(25);

  const [editando, setEditando] = useState<Generador | null | undefined>(undefined);
  const clienteDeConsultas = useQueryClient();
  const { puede } = useSesion();
  const puedeGestionar = puede('generadores.gestionar');

  const buscado = useValorDiferido(texto.trim());

  const tarifario = useQuery({
    queryKey: ['generadores', 'tarifario'],
    queryFn: () => api.get<Tarifario>('/generadores/tarifario'),
  });

  const listado = useQuery({
    queryKey: ['generadores', buscado, categoria, estado, pagina, tamanio],
    queryFn: () =>
      api.get<Pagina<Generador>>('/generadores', {
        query: { texto: buscado, categoria, activo: estado, pagina, tamanio },
      }),
    placeholderData: (anterior) => anterior,
  });

  function reiniciar<V>(fijar: (v: V) => void) {
    return (v: V) => {
      fijar(v);
      setPagina(0);
    };
  }

  const columnas: Columna<Generador>[] = [
    {
      clave: 'codigo',
      encabezado: 'Código',
      contenido: (g) => <span className="importe text-xs text-armazon-500">{g.codigo}</span>,
    },
    {
      clave: 'generador',
      encabezado: 'Generador',
      contenido: (g) => (
        <span className="block min-w-0">
          <span className="block truncate text-xs font-medium uppercase text-armazon-800">
            {g.nombre}
          </span>
          {g.sucursal && (
            <span className="block truncate text-[10px] font-semibold uppercase text-marca-700">
              Sucursal · {g.sucursal}
            </span>
          )}
          {g.nombreComercial && (
            <span className="block truncate text-[10px] uppercase text-armazon-500">
              {g.nombreComercial}
            </span>
          )}
        </span>
      ),
    },
    {
      clave: 'ruc',
      encabezado: 'RUC',
      contenido: (g) => (
        <span className="importe text-xs text-armazon-600">{g.identificacion}</span>
      ),
    },
    {
      clave: 'categoria',
      encabezado: 'Categoría',
      contenido: (g) => (
        <span className="inline-flex items-center gap-1.5">
          <span className="grid size-5 shrink-0 place-items-center rounded bg-marca-100 text-[10px] font-bold uppercase text-marca-800">
            {g.literal}
          </span>
          <span className="text-[11px] uppercase text-armazon-700">{g.categoria}</span>
        </span>
      ),
    },
    {
      clave: 'distancia',
      encabezado: 'Distancia',
      alinear: 'derecha',
      contenido: (g) =>
        g.recargoPorDistancia ? (
          <span className="importe text-xs text-armazon-700">{g.distanciaKm.toFixed(2)} km</span>
        ) : (
          <span className="text-[11px] text-armazon-300">—</span>
        ),
    },
    {
      clave: 'estado',
      encabezado: 'Estado',
      alinear: 'centro',
      contenido: (g) => (
        <span
          className={`rounded-md px-2 py-0.5 text-[10px] font-bold uppercase ${
            g.activo ? 'bg-marca-100 text-marca-800' : 'bg-armazon-100 text-armazon-500'
          }`}
        >
          {g.activo ? 'Activo' : 'Inactivo'}
        </span>
      ),
    },
    {
      clave: 'acciones',
      encabezado: '',
      alinear: 'derecha',
      contenido: (g) =>
        puedeGestionar && (
          <BotonDeFila icono={Pencil} titulo="Editar" alPulsar={() => setEditando(g)} />
        ),
    },
  ];

  const categorias = tarifario.data?.categorias ?? [];

  return (
    <>
      <EncabezadoDePagina
        icono={Factory}
        titulo="Catastro de generadores"
        subtitulo="Quienes pagan por el peso de sus residuos · Art. 9 literales b, c y d"
        acciones={
          puedeGestionar && (
            <button
              type="button"
              onClick={() => setEditando(null)}
              disabled={categorias.length === 0}
              className="inline-flex h-10 items-center gap-2 rounded-lg bg-marca-600 px-4 text-xs font-bold uppercase text-white shadow-sm transition-colors hover:bg-marca-700 disabled:opacity-50"
            >
              <Plus className="size-4" aria-hidden />
              Nuevo generador
            </button>
          )
        }
      />

      <Tarifas tarifario={tarifario.data} />

      <Tabla
        datos={listado.data}
        cargando={listado.isPending}
        columnas={columnas}
        tamanio={tamanio}
        alCambiarPagina={setPagina}
        alCambiarTamanio={reiniciar(setTamanio)}
        entidad="generadores"
        iconoVacio={Factory}
        mensajeVacio="Ningún generador coincide"
        herramientas={
          <span className="flex flex-wrap items-center gap-2">
            <CampoDeBusqueda
              valor={texto}
              alCambiar={reiniciar(setTexto)}
              marcador="Razón social, nombre comercial, RUC o código…"
              className="w-80"
            />
            <select
              value={categoria}
              onChange={(e) => reiniciar(setCategoria)(e.target.value)}
              aria-label="Categoría"
              className={SELECTOR}
            >
              <option value="">TODAS LAS CATEGORÍAS</option>
              {categorias.map((c) => (
                <option key={c.id} value={c.id}>
                  {`${c.literal.toUpperCase()}) ${c.nombre}`}
                </option>
              ))}
            </select>
            <select
              value={estado}
              onChange={(e) => reiniciar(setEstado)(e.target.value)}
              aria-label="Estado"
              className={SELECTOR}
            >
              <option value="true">ACTIVOS</option>
              <option value="false">INACTIVOS</option>
              <option value="">TODOS</option>
            </select>
          </span>
        }
      />

      {editando !== undefined && tarifario.data && (
        <ModalDeGenerador
          generador={editando}
          categorias={categorias}
          umbralKm={tarifario.data.umbralKm}
          alCerrar={() => setEditando(undefined)}
          alGuardar={() => {
            setEditando(undefined);
            void clienteDeConsultas.invalidateQueries({ queryKey: ['generadores'] });
          }}
        />
      )}
    </>
  );
}

function Tarifas({ tarifario }: { tarifario: Tarifario | undefined }) {
  if (!tarifario) {
    return <div className="mb-5 h-24 animate-pulse rounded-xl bg-armazon-100" />;
  }
  return (
    <div className="mb-5">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {tarifario.categorias.map((c) => (
          <TarjetaDeCategoria key={c.id} categoria={c} />
        ))}
      </div>
      <p className="mt-2 text-[10px] uppercase text-armazon-400">
        Con el SBU de hoy, ${tarifario.sbu.toFixed(2)}. Cada ciclo mensual congela el suyo al
        abrirse. Recargo por distancia en el sector salud: ${tarifario.recargoPorKm.toFixed(2)} por
        km pasados los {tarifario.umbralKm} km de EPAGAL.
      </p>
    </div>
  );
}

function TarjetaDeCategoria({ categoria: c }: { categoria: CategoriaDeGenerador }) {
  return (
    <div className="rounded-xl border border-armazon-200 bg-white p-4">
      <div className="flex items-start justify-between gap-2">
        <span className="text-[10px] font-semibold uppercase leading-tight text-armazon-500">
          Literal {c.literal}) · {c.porcentajeSbu} % del SBU
        </span>
        <span className="shrink-0 rounded bg-armazon-100 px-1.5 text-[10px] font-bold uppercase text-armazon-600">
          {c.generadores} activos
        </span>
      </div>
      <p className="mt-1 text-xs font-bold uppercase leading-snug text-armazon-800">{c.nombre}</p>
      <p className="importe mt-2 text-xl font-bold text-marca-700">
        ${c.tarifaUnitaria.toFixed(2)}
        <span className="text-xs font-semibold text-armazon-500">
          {' '}
          / {enUnidad(c.unidad, true)}
        </span>
      </p>
    </div>
  );
}

const SELECTOR =
  'h-9 rounded-lg border border-armazon-200 bg-white px-2 text-xs uppercase text-armazon-600 outline-none transition-colors focus:border-marca-400';
